/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Syncs", description = "An overview of your syncs along with their status, which model they use, the destination they write to, the schedule, last run, and when they were created.", lastUpdatedDate = new Date(1706140800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Syncs",
        description: "An overview of your syncs along with their status, which model they use, the destination they write to, the schedule, last run, and when they were created.",
        lastUpdatedDate: "Thu Jan 25 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Syncs overview page",
          "depth": 0,
          "id": "syncs-overview-page"
        }, {
          "value": "Sync status",
          "depth": 1,
          "id": "sync-status"
        }, {
          "value": "Individual sync overview page",
          "depth": 0,
          "id": "individual-sync-overview-page"
        }, {
          "value": "Resync full query",
          "depth": 1,
          "id": "resync-full-query"
        }, {
          "value": "Clear and fill",
          "depth": 1,
          "id": "clear-and-fill"
        }, {
          "value": "Reset CDC",
          "depth": 1,
          "id": "reset-cdc"
        }, {
          "value": "Run details",
          "depth": 1,
          "id": "run-details"
        }, {
          "value": "Configuration",
          "depth": 1,
          "id": "configuration"
        }, {
          "value": "Schedule",
          "depth": 1,
          "id": "schedule"
        }, {
          "value": "Alerts",
          "depth": 1,
          "id": "alerts"
        }, {
          "value": "Sync logs",
          "depth": 1,
          "id": "sync-logs"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      img: "img",
      a: "a",
      h2: "h2",
      strong: "strong",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      em: "em",
      pre: "pre",
      code: "code",
      span: "span"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Syncs declare how you want query results from a model to appear in your destination."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "welcome/sync-to-destination.png",
          alt: "Sync to Destination"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To learn more about syncs on a conceptual level, see the ", _jsx(_components.a, {
          href: "/getting-started/concepts#syncs",
          children: "core concepts page"
        }), ". Refer to destination docs for specific configuration details."]
      }), "\n", _jsx(_components.p, {
        children: "To learn more about the information the Hightouch UI displays on your syncs and what actions you can take from the UI, continue to the following sections."
      }), "\n", _jsx(_components.h2, {
        id: "syncs-overview-page",
        children: "Syncs overview page"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch displays all syncs, both active and inactive on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview"]
        }), " page."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/syncs-overview.png",
          alt: "Sync overview page in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "For each sync, the overview shows:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The sync's ", _jsx(_components.a, {
            href: "#sync-status",
            children: _jsx(_components.strong, {
              children: "status"
            })
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["The sync's associated ", _jsx(_components.a, {
            href: "/getting-started/concepts#models",
            children: _jsx(_components.strong, {
              children: "model"
            })
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["The sync's associated ", _jsx(_components.a, {
            href: "/getting-started/concepts#destinations",
            children: _jsx(_components.strong, {
              children: "destination"
            })
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["The date and time, in your timezone, of the sync's ", _jsx(_components.strong, {
            children: "last run"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["The date the sync was ", _jsx(_components.strong, {
            children: "created at"
          }), ", and who created the sync"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can view more in-depth information for any sync by clicking on it. This takes you to that individual sync's ", _jsx(_components.a, {
          href: "#sync-overview",
          children: "overview page"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "sync-status",
        children: "Sync status"
      }), "\n", _jsx(_components.p, {
        children: "Your sync's status is based on the latest information available on a run:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Healthy"
          }), ": a recent run completed"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Pending"
          }), ": no runs have been scheduled yet"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Disabled"
          }), ": the sync has been turned off"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Querying"
          }), ": the sync is querying its associated model"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Preparing"
          }), ": the sync is preparing data to send to its associated destination"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Queued"
          }), ": the sync is scheduled to begin writing to its destination"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "In Progress"
          }), ": the sync is writing to its destination"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Incomplete"
          }), ": the sync had a temporary interruption and will resume in a new run"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Aborted/Failed"
          }), ": there was an error in the sync progress—check the errors on the sync to see why the sync failed"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "individual-sync-overview-page",
        children: "Individual sync overview page"
      }), "\n", _jsxs(_components.p, {
        children: ["You can view an individual sync's information by clicking on it on the on the general ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview"]
        }), " page."]
      }), "\n", _jsx(_components.p, {
        children: "A particular sync's overview page shows general information, including:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The sync's associated model and destination"
        }), "\n", _jsxs(_components.li, {
          children: ["Whether the sync is currently ", _jsx(_components.strong, {
            children: "enabled"
          }), " or ", _jsx(_components.strong, {
            children: "disabled"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["The option to manually ", _jsx(_components.strong, {
            children: "Run"
          }), " the sync"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also choose to move the sync to a ", _jsx(_components.a, {
          href: "/workspace-management/folders-filters#folders",
          children: "folder"
        }), ", edit its ", _jsx(_components.a, {
          href: "/workspace-management/lbac#what-are-labels",
          children: "labels"
        }), ", ", _jsx(_components.a, {
          href: "#resync-full-query",
          children: "resync the full query results"
        }), " of a model, ", _jsx(_components.a, {
          href: "#reset-cdc",
          children: "reset change data capture"
        }), ", or delete a sync from here."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sync-overview.png",
          alt: "Sync information"
        })
      }), "\n", _jsx(_components.p, {
        children: "The overview page also shows more specific information including:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync ID"
          }), ": a numerical unique identifier for the sync that is necessary when setting up an ", _jsx(_components.a, {
            href: "/extensions/airflow",
            children: "Airflow"
          }), " operator, or using the ", _jsx(_components.a, {
            href: "https://github.com/hightouchio/dagster-hightouch",
            children: "Dagster"
          }), ", ", _jsx(_components.a, {
            href: "https://github.com/PrefectHQ/prefect-hightouch",
            children: "Prefect"
          }), ", or ", _jsx(_components.a, {
            href: "https://docs.mage.ai/integrations/hightouch",
            children: "Mage"
          }), " extensions"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync schedule type"
          }), ": manual, interval, custom, cron, dbt Cloud-, or Fivetran-triggered"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync mode"
          }), ": this only appears if there are multiple sync mode options, see the ", _jsx(_components.a, {
            href: "/syncs/types-and-modes#sync-modes",
            children: "sync mode docs"
          }), " for more information"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Match rate"
          }), ": this only appears if the sync is to a paid advertising destination, see the ", _jsx(_components.a, {
            href: "/customer-studio/syncs#view-match-rates",
            children: "match rate docs"
          }), " docs for more information"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Any associated labels"
          }), ": see ", _jsx(_components.a, {
            href: "/workspace-management/lbac",
            children: "label based access-controls"
          }), " for more information"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sync-details.png",
          alt: "Sync information"
        })
      }), "\n", _jsx(_components.p, {
        children: "Finally, you can click the tabs on the overview page to view and edit settings:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Runs"
          }), ": You can see the status, time, and ", _jsx(_components.a, {
            href: "#run-details",
            children: "more details"
          }), " from the most recent runs. Selecting a particular run brings you to the ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/syncs/debugger",
            children: "live debugger"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Configuration"
          }), ": You can view and edit the sync's ", _jsx(_components.a, {
            href: "#configuration",
            children: "configuration"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Schedule"
          }), ": You view and edit the sync's run ", _jsx(_components.a, {
            href: "#schedule",
            children: "schedule"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Alerts"
          }), ": You view and edit the sync's ", _jsx(_components.a, {
            href: "/syncs/alerting",
            children: "alerts"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync logs"
          }), ": You can view and edit where your ", _jsx(_components.a, {
            href: "/syncs/warehouse-sync-logs",
            children: "sync's historical logs"
          }), " are stored."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Activity"
          }), ": You can view recent changes to the sync configuration and other settings"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sync-overview-tabs.png",
          alt: "Sync information"
        })
      }), "\n", _jsx(_components.h3, {
        id: "resync-full-query",
        children: "Resync full query"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch keeps track of changes to your data between syncs and ", _jsx(_components.a, {
          href: "/getting-started/concepts#change-data-capture",
          children: "only sends necessary updates"
        }), ".\nTherefore, an initial sync run ", _jsx(_components.a, {
          href: "/syncs/debugger#view-run-details",
          children: "often shows"
        }), " that many records were ", _jsx(_components.strong, {
          children: "Added"
        }), ". Subsequent runs often show fewer records ", _jsx(_components.strong, {
          children: "Added"
        }), ", and instead show that many records have ", _jsx(_components.strong, {
          children: "Changed"
        }), " or ", _jsx(_components.strong, {
          children: "Removed"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you wish to reset the sync's tracking to its initial state—as if it were syncing for the first time again—you can select ", _jsx(_components.strong, {
          children: "Resync full query"
        }), " from the menu on the sync's main page.\nBefore you do, make sure that your sync meets ", _jsx(_components.a, {
          href: "#full-resync-prerequisites",
          children: "the prerequisites"
        }), " to safely do so."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/resync-full-query.png",
          alt: "Resync full query"
        })
      }), "\n", _jsx(_components.h4, {
        id: "full-resync-prerequisites",
        children: "Full resync prerequisites"
      }), "\n", _jsxs(_components.p, {
        children: ["Full resyncs process all rows that currently exist in the model as ", _jsx(_components.strong, {
          children: "Added"
        }), " rows. Check the following before triggering a full resync:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Criteria"
            }), _jsx(_components.th, {
              children: "Explanation"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/types-and-modes#sync-modes",
                children: "Sync mode"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["Syncs set to ", _jsxs(_components.a, {
                    href: "/syncs/types-and-modes#sync-modes",
                    children: [_jsx(_components.strong, {
                      children: "update"
                    }), " or ", _jsx(_components.strong, {
                      children: "upsert"
                    }), " mode"]
                  }), " are safe to fully resync because if a row was already synced, Hightouch updates the same record."]
                }), _jsxs("li", {
                  children: [_jsxs(_components.a, {
                    href: "/syncs/types-and-modes#sync-modes",
                    children: [_jsx(_components.strong, {
                      children: "Insert"
                    }), " mode"]
                  }), " syncs, ", _jsx(_components.a, {
                    href: "/syncs/types-and-modes#event-syncs",
                    children: "event syncs"
                  }), ", and ", _jsx(_components.a, {
                    href: "/destinations/http-request",
                    children: "webhook destinations"
                  }), " aren't recommended for full resyncs because reprocessing rows can create duplicates."]
                })]
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#delete-behavior",
                children: "Delete behavior"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["If your destination allows for deleting records, and you selected to ", _jsx(_components.strong, {
                    children: "Do nothing"
                  }), " when records leave the model query result set, a full resync will have no impact. "]
                }), _jsxs("li", {
                  children: ["If you selected ", _jsx(_components.strong, {
                    children: "Clear fields"
                  }), " or ", _jsx(_components.strong, {
                    children: "Delete destination record"
                  }), ", this setting gets ignored during a full resync. This is because full resyncs only process rows that currently exist in the model and run the sync as if it were the initial run. This means that there will be no ", _jsx(_components.a, {
                    href: "/getting-started/concepts#change-data-capture",
                    children: "diffing"
                  }), " or comparison to the prior run to identify what has been removed. Therefore,  all ", _jsx(_components.strong, {
                    children: "Removed"
                  }), " rows (in comparison to the previous sync run) are ignored even if delete behavior is enabled."]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Resyncing the full query could create duplicates or other issues in your destination data if the prerequisites above haven't been met."
        })
      }), "\n", _jsx(_components.h3, {
        id: "clear-and-fill",
        children: "Clear and fill"
      }), "\n", _jsxs(_components.p, {
        children: ["If the destination supports removing all records in a segment or audience, this option will become available. This resync mode is similar to a ", _jsx(_components.a, {
          href: "#resync-full-query",
          children: "full resync"
        }), " mentioned above with an additional preprocessing step.\nBefore processing all rows that are currently in the model, the segment/audience selected in the sync configuration will be cleared of all users/identifiers."]
      }), "\n", _jsx(_components.h3, {
        id: "reset-cdc",
        children: "Reset CDC"
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to reset ", _jsx(_components.a, {
          href: "/getting-started/concepts#change-data-capture",
          children: "change data capture"
        }), " without resyncing records, you can select the \"Reset CDC\" option instead."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/reset-cdc.png",
          alt: "Reset CDC"
        })
      }), "\n", _jsx(_components.p, {
        children: "This instructs Hightouch to ignore the current state of your model and to track only rows that are added, changed, or removed in the future. The next sync run will be used as an opportunity to capture a snapshot of your model, but no data will be sent to the destination. Thereafter, future sync runs will track new changes only."
      }), "\n", _jsx(_components.p, {
        children: "The \"Reset CDC\" option is recommended for insert-only use cases like conversion events, operational alerts, and other situations where a backfill would create undesired duplicate records. Beware that skipping the backfill may cause your destination to drift out of sync with your data model."
      }), "\n", _jsx(_components.h3, {
        id: "run-details",
        children: "Run details"
      }), "\n", _jsx(_components.p, {
        children: "When viewing a specific sync, you can inspect detailed run-level information. A run is a particular invocation of a sync, either triggered manually or scheduled."
      }), "\n", _jsx(_components.p, {
        children: "Each run provides:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The particular run's ", _jsx(_components.strong, {
            children: "status"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["The time the run ", _jsx(_components.strong, {
            children: "started"
          }), " and its duration in seconds"]
        }), "\n", _jsxs(_components.li, {
          children: ["The number of ", _jsx(_components.strong, {
            children: "rows queried"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["The total number of ", _jsx(_components.strong, {
            children: "operations"
          }), ", including rows added, changed, removed"]
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "results"
          }), "—the total number of successful operations in green and the number of rejected operations in red"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sync-run.png",
          alt: "Sync run table"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If there were any errors, you can click the ", _jsx(_components.strong, {
          children: "View Error"
        }), " button to view details related to the run."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/row-error.png",
          alt: "Rejected rows"
        })
      }), "\n", _jsx(_components.h3, {
        id: "configuration",
        children: "Configuration"
      }), "\n", _jsx(_components.p, {
        children: "The configuration section exposes destination-specific configuration options. While sync configurations vary depending on the destination, they generally include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Type"
          }), ": What data type to sync to in your destination; for example, objects, events, subscription lists, etc. To learn more, see sync ", _jsx(_components.a, {
            href: "/syncs/types-and-modes",
            children: "types and modes"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Mode"
          }), ": What update mode to use; for example, upsert, insert, update, etc. To learn more, see sync ", _jsx(_components.a, {
            href: "/syncs/types-and-modes",
            children: "types and modes"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Record matching"
          }), " If updating or upserting, how rows in the source should be matched to records in the destination. To learn more, refer to the ", _jsx(_components.a, {
            href: "/syncs/record-matching",
            children: "record matching docs"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Field mapping"
          }), ": What columns from your source should be synced to the destination, and what destination fields they should appear as. To learn more, refer to the ", _jsx(_components.a, {
            href: "/syncs/mapping-data",
            children: "field mapping docs"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Batching"
          }), ": How data should be batched in API requests."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Delete behavior"
          }), ": What to do when a record leaves your source—in other words, when your model no longer returns a record that was returned in the previous sync run."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "For some sync types and destinations, you may want to delete records if they no longer appear in your model's query results."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, you may be syncing customer records to your CRM based on a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " that queries all active users from your data warehouse. If some users become inactive—in other words, they no longer appear in your model's query results—you may want to delete them or some information about them from your CRM."]
      }), "\n", _jsx(_components.p, {
        children: "Depending on the destination and sync mode, these are common options for delete behavior:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsxs(_components.td, {
              children: ["Preserve the record in the destination. This option is useful for when you want your destination to continually ", _jsx(_components.strong, {
                children: "accrue"
              }), " records from your source."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear fields"
              })
            }), _jsx(_components.td, {
              children: "Keep the record, but clear the mapped fields. This option useful to retain records in your destination, but clear the fields that are mapped in your sync."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete destination record"
              })
            }), _jsxs(_components.td, {
              children: ["Delete the record in the destination. This option is useful for when you want your destination to exactly ", _jsx(_components.strong, {
                children: "mirror"
              }), " your source by dropping records that have dropped out of your model."]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch only acts on records as they leave your model's query results, and ", _jsx(_components.em, {
          children: "after"
        }), " you apply deletion settings. Hightouch doesn't have access to historically removed records, so syncs can't delete records or clear fields that have already been removed from your source. Hightouch only deletes records that left your source in comparison to the immediately previous run. A ", _jsx(_components.a, {
          href: "#resync-full-query",
          children: "full resync"
        }), " doesn't delete or clear historically removed records."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Regardless of the option you choose for delete behavior, a ", _jsx(_components.a, {
            href: "/syncs/debugger#view-run-details",
            children: "sync run's detail page"
          }), " always displays removed rows."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Check out the particular destination's documentation for its delete behavior configuration details."
      }), "\n", _jsx(_components.h4, {
        id: "configuration-actions",
        children: "Configuration actions"
      }), "\n", _jsxs(_components.p, {
        children: ["When working with a sync configuration, you can also choose to ", _jsx(_components.a, {
          href: "#test-a-row",
          children: "test a row"
        }), " or edit the configuration ", _jsx(_components.a, {
          href: "#edit-as-json",
          children: "as a JSON"
        }), " object."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sync-configuration.png",
          alt: "Sync configuration page in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "test-a-row",
        children: "Test a row"
      }), "\n", _jsx(_components.p, {
        children: "You can test a sync by sending just one row from your model to your destination. This lets you verify that the sync is working as intended before initiating a run of your entire sync."
      }), "\n", _jsxs(_components.p, {
        children: ["From your sync's configuration page, click ", _jsx(_components.strong, {
          children: "Test"
        }), " and select a row to sync.\nBefore testing a row, you can edit the columns’ data types and values by hovering over them.\nClick the pencil icon, make your edit, and then click the check mark to save any changes.\nEditing the model column used for ", _jsx(_components.a, {
          href: "/syncs/record-matching#model-column-selection",
          children: "record matching"
        }), " is not supported since Hightouch ", _jsx(_components.a, {
          href: "/models/data-types-casting",
          children: "intentionally strigifies"
        }), " it for enhanced performance."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/test-a-row-1.png",
          alt: "Configuring the test a row window"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The row testing window only displays certain ", _jsx(_components.a, {
            href: "/models/data-types-casting#data-types",
            children: "data types"
          }), ", such as ", _jsx(_components.strong, {
            children: "string"
          }), " and ", _jsx(_components.strong, {
            children: "number"
          }), ".\nIf a model contains a column with a different type, such as ", _jsx(_components.strong, {
            children: "date"
          }), ", the UI displays the type as ", _jsx(_components.strong, {
            children: "string"
          }), ".\nThis only concerns the UI—Hightouch doesn't convert the data type to string when syncing data.\nTo confirm a column's data type, refer to its type on the ", _jsx(_components.a, {
            href: "/models/data-types-casting#view-model-data-types",
            children: "model configuration"
          }), " page."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can then decide to sync the row as an ", _jsx(_components.a, {
          href: "/syncs/debugger#view-run-details",
          children: "added, changed, or removed row"
        }), " and observe the API requests and responses.\nThe UI might take a few seconds to update before displaying the results."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/test-a-row-2.png",
          alt: "Testing a row in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "edit-as-json",
        children: "Edit as JSON"
      }), "\n", _jsx(_components.p, {
        children: "You can edit your sync's configurations as JSON code to quickly make multiple changes or copy and paste one sync's configurations to another."
      }), "\n", _jsx(_components.p, {
        children: "For example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-JSON",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"mode\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"upsert\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"object\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"fromId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"track_visitor_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"object\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"mappings\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"to\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"uuid-western\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"from\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"uuid\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"to\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Trialer Status\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"from\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"trialer_status\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customMappings\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"to\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"unique_notification_status\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"from\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"notify_status\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "schedule",
        children: "Schedule"
      }), "\n", _jsx(_components.p, {
        children: "Part of sync configuration is deciding how frequently the sync should run. Besides triggering syncs manually, you can schedule syncs with these options:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Interval"
          }), ": You can schedule your sync to run on a set interval, for example once every day."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom recurrence"
          }), ": You can schedule your sync to run a specific date and time, for example every Monday at 9 AM."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Cron expression"
          }), ": You can schedule your sync using a ", _jsx(_components.a, {
            href: "https://en.wikipedia.org/wiki/Cron",
            children: "cron expression"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "dbt Cloud"
          }), ": You can trigger syncs automatically via ", _jsx(_components.a, {
            href: "/extensions/dbt-cloud",
            children: "dbt Cloud"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Fivetran"
          }), ": You can trigger syncs automatically via ", _jsx(_components.a, {
            href: "/extensions/fivetran",
            children: "Fivetran"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sync-schedule.png",
          alt: "Sync scheduling in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Though not listed as schedule options, you can also trigger syncs using ", _jsx(_components.a, {
          href: "/extensions/airflow",
          children: "Airflow"
        }), ", ", _jsx(_components.a, {
          href: "https://github.com/hightouchio/dagster-hightouch",
          children: "Dagster"
        }), ", ", _jsx(_components.a, {
          href: "https://docs-v1.prefect.io/api/latest/tasks/hightouch.html",
          children: "Prefect"
        }), ", ", _jsx(_components.a, {
          href: "https://docs.mage.ai/integrations/hightouch",
          children: "Mage"
        }), ", or the ", _jsx(_components.a, {
          href: "/api-reference",
          children: "REST API"
        }), ". To learn more about your scheduling options, refer to the ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui",
          children: "sync scheduling page"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you have dependencies between syncs, for example you require one sync to complete before another begins, sync ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-with-sequences",
          children: "Sequences"
        }), " can help you manage scheduling."]
      }), "\n", _jsx(_components.h3, {
        id: "alerts",
        children: "Alerts"
      }), "\n", _jsxs(_components.p, {
        children: ["You can configure ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: "alerting"
        }), " on a per-sync basis from this section."]
      }), "\n", _jsx(_components.h3, {
        id: "sync-logs",
        children: "Sync logs"
      }), "\n", _jsx(BtOnly, {
        featureName: "Warehouse Sync Logs",
        plural: true
      }), "\n", _jsxs(_components.p, {
        children: ["You can configure ", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "Warehouse Sync Logs"
        }), " from this section. This feature writes information on the results of your syncs back into your warehouse so that you can perform more complex analysis."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
